<sbnb-full-screen-modal [closeLink]="closeLink">
  @switch (currentStep) {
    @case (DomainWizardStep.SELECT_TYPE) {
      <h2 class="text-2xl font-medium mb-8">What kind of domain would you like to add?</h2>

      <!-- Connect existing domain option -->
      <div
        (click)="selectDomainType('connect')"
        class="mb-4 p-6 border border-smoke rounded-lg cursor-pointer hover:border-grass-darker transition-colors">
        <h3 class="text-xl mb-3">Connect my existing domain</h3>
        <span>I already have a domain I'd like to use</span>
      </div>

      <!-- Buy new domain option -->
      <div class="mb-4 p-6 border border-smoke rounded-lg relative opacity-70 cursor-not-allowed">
        <h3 class="text-xl mb-4 flex items-center gap-4">
          <span>Buy a new domain</span>
          <sbnb-badge context="warning" volume="high">Coming soon!</sbnb-badge>
        </h3>

        <div class="flex items-center gap-2">
          <span>Purchase and configure a new domain directly through Hospitable</span>
        </div>
      </div>

      <div class="mt-8 flex justify-end w-full">
        <sbnb-button label="Continue" (click)="continue()"></sbnb-button>
      </div>
    }

    @case (DomainWizardStep.ENTER_DOMAIN) {
      @if (domainType === 'connect') {
        <section class="title-section">
          <h2 class="mb-0">Connect existing domain</h2>
          <p class="subheader">Enter the domain name that you want to connect</p>
        </section>

        <form [formGroup]="domainForm">
          <mat-form-field class="fw nopadding">
            <input
              autofocus
              matInput
              type="text"
              formControlName="domainName"
              placeholder="example.com"
              data-attribute="domain-input"
              (keyup.enter)="$event.stopPropagation(); $event.preventDefault(); !isLoading && continue()" />
          </mat-form-field>

          <div class="button-container mt-10 flex justify-between gap-2">
            <sbnb-button
              htmlButtonType="button"
              label="Back"
              type="secondary"
              [disabled]="isLoading"
              (click)="goBack()"></sbnb-button>

            <sbnb-button
              class="next-step"
              label="Continue"
              [fullWidth]="true"
              [loading]="isLoading"
              [disabled]="!domainForm.valid || isLoading"
              data-attribute="add-domain-button"
              (click)="continue()"></sbnb-button>
          </div>
        </form>
      }

      @if (domainType === 'purchase') {
        <article class="main">
          <section class="title-section">
            <h2 class="title">Buy a new domain</h2>
            <p class="subheader">Search for your perfect domain name</p>
          </section>

          <section class="content">
            <!-- Domain Search -->
            <div class="domain-search">
              <div class="search-input-container">
                <span class="search-icon material-icons">search</span>
                <input
                  type="text"
                  class="search-input"
                  placeholder="Search for a domain name..."
                  [(ngModel)]="domainSearchQuery"
                  (keyup.enter)="searchDomains()" />
              </div>

              <!-- Search Results -->
              @if (searchResults.length > 0 || searchError) {
                <div class="search-results">
                  <!-- Error Message -->
                  @if (searchError) {
                    <div class="search-error">
                      <span class="material-icons text-red-500">error</span>
                      <div class="error-message">
                        <p>{{ searchError.message }}</p>
                        <p class="error-suggestion">{{ searchError.suggestion }}</p>
                      </div>
                    </div>
                  }

                  <!-- Available Domains -->
                  @for (domain of searchResults; track domain.name) {
                    <div class="domain-item">
                      <div class="domain-info">
                        <span class="domain-availability">
                          <span class="material-icons text-green-500">check_circle</span>
                        </span>
                        <span class="domain-name">{{ domain.name }}</span>
                        <span class="domain-price">${{ domain.price.toFixed(2) }}/year</span>
                      </div>
                      <button class="buy-button" (click)="selectDomainToPurchase(domain)">Buy</button>
                    </div>
                  }
                </div>
              }
            </div>
          </section>
        </article>
      }
    }

    @case (DomainWizardStep.DOMAIN_PURCHASE) {
      <h2 class="title">Buy new domain</h2>

      <section class="content">
        <div class="purchase-container">
          <!-- Left Column - Domain Details -->
          <div class="domain-details">
            <h3 class="domain-name">{{ selectedDomain.name }}</h3>

            <div class="auto-renew">
              <span>Auto-renew every year</span>
              <mat-slide-toggle [(ngModel)]="autoRenew"></mat-slide-toggle>
            </div>

            <div class="contact-info">
              <div class="contact-header">
                <h3>Contact information</h3>
                <button class="edit-button" (click)="editContactInfo()">
                  <span class="material-icons">edit</span>
                </button>
              </div>

              <div class="contact-details">
                <p>{{ contactInfo.name }}</p>
                <p>{{ contactInfo.address }}</p>
                <p>{{ contactInfo.city }}</p>
                <p>{{ contactInfo.country }}</p>
                <p>{{ contactInfo.phone }}</p>
              </div>

              <div class="whois-privacy">
                <p>
                  WHOIS privacy: This domain includes WHOIS privacy. Your contact information won't be displayed in
                  public domain registration records.
                </p>
              </div>
            </div>
          </div>

          <!-- Right Column - Order Summary -->
          <div class="order-summary">
            <h3>Summary</h3>

            <div class="summary-item">
              <span>Today</span>
              <span>${{ selectedDomain.price.toFixed(2) }}</span>
            </div>
            <div class="summary-detail">Domain purchase</div>

            <div class="summary-item">
              <span>Every year</span>
              <span>${{ selectedDomain.price.toFixed(2) }}</span>
            </div>
            <div class="summary-detail">Annual renewal</div>

            <div class="terms-checkbox">
              <mat-checkbox [(ngModel)]="termsAccepted">
                I understand that ${{ selectedDomain.price.toFixed(2) }} will be added to my next invoice and will be
                billed annually
              </mat-checkbox>
            </div>

            <sbnb-button
              class="purchase-button"
              label="Buy domain"
              [disabled]="!termsAccepted"
              [fullWidth]="true"
              (click)="purchaseDomain()"></sbnb-button>

            <div class="terms-text">
              <p>
                By buying this domain, you have read and agree to the
                <a href="https://icann.org" target="_blank">ICANN Policy</a>
                and
                <a href="#" target="_blank">Domain Registration Agreement</a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    }

    @case (DomainWizardStep.SELECT_DNS_MANAGEMENT) {
      @if (!isLoading) {
        <h2 class="mb-8">How do you want to connect {{ domainName }} to Hospitable?</h2>

        <div class="flex flex-col gap-2" (keyup.enter)="continue()">
          <sbnb-radio-card
            [selected]="dnsManagement === 'hospitable'"
            (click)="dnsManagement = 'hospitable'"
            class="p-2">
            <h3 class="text-lg font-medium mb-4 flex items-center gap-2">
              <span>Let Hospitable manage DNS for you</span>
              <sbnb-badge context="positive" volume="high">Recommended</sbnb-badge>
            </h3>
            <span>You'll update your domain's name servers to use our platform's DNS</span>

            <ul class="flex flex-col gap-2 mt-4 ml-0 pl-0 space-y-2 text-sm">
              <li class="flex items-center gap-2">
                <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
                <span>We'll automatically configure DNS records</span>
              </li>
              <li class="flex items-center gap-2">
                <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
                <span>You can manage subdomains and DNS settings right from Hospitable</span>
              </li>
              <li class="flex items-center gap-2">
                <sbnb-icon icon="warning" class="shrink-0"></sbnb-icon>
                <span>
                  If you already use custom DNS settings (eg for email hosting), switching name servers may cause
                  disruptions
                </span>
              </li>
            </ul>
          </sbnb-radio-card>

          <sbnb-radio-card [selected]="dnsManagement === 'self'" (click)="dnsManagement = 'self'" class="p-2">
            <h3 class="text-lg font-medium mb-4">I'll manage my own DNS</h3>
            <span>You'll keep your existing DNS provider & name servers</span>

            <ul class="flex flex-col gap-2 mt-4 ml-0 pl-0 space-y-2 text-sm">
              <li class="flex items-center gap-2">
                <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
                <span>You'll keep your existing DNS provider & name servers</span>
              </li>
              <li class="flex items-center gap-2">
                <sbnb-icon icon="check" class="text-grass-darker"></sbnb-icon>
                <span>You'll need to manually add records with your domain provider</span>
              </li>
            </ul>
          </sbnb-radio-card>
        </div>

        <div class="button-container mt-10 flex justify-between gap-2">
          <sbnb-button label="Back" type="secondary" (click)="currentStep = DomainWizardStep.SELECT_TYPE"></sbnb-button>
          <sbnb-button class="next-step" label="Continue" [fullWidth]="true" (click)="continue()"></sbnb-button>
        </div>
      }

      @if (isLoading) {
        <div class="flex items-center gap-4">
          <mat-spinner [diameter]="32"></mat-spinner>
          <span>Adding your domain...</span>
        </div>
      }
    }

    @case (DomainWizardStep.COMPLETE) {
      <h2 class="mb-4">Domain Added! 🎉</h2>

      <div class="flex items-center gap-2">
        <span class="leading-6">
          {{ domainName || 'Your domain' }} has been successfully added to your Hospitable account.
          {{
            dnsManagement === 'hospitable'
              ? 'Once the name server changes propagate, your domain will be fully configured.'
              : 'Your DNS records have been verified.'
          }}
        </span>
      </div>

      <sbnb-alert-card class="mt-8">
        <div class="flex flex-col gap-2 p-2">
          <div class="flex strong items-center gap-2">
            <sbnb-icon icon="details"></sbnb-icon>
            <span>What's Next?</span>
          </div>

          <span class="leading-6">
            You can now use this domain for your direct booking site. Go to your site settings to assign this domain to
            your booking site.
          </span>
        </div>
      </sbnb-alert-card>

      <div class="flex justify-end mt-8">
        <sbnb-button class="next-step" label="Finish" (click)="continue()"></sbnb-button>
      </div>
    }
  }
</sbnb-full-screen-modal>
